// src/components/Library.js
import { getUserExercises, getUserWorkouts } from "../../utils/saveWorkout";
import { unsaveExercise } from "../../utils/saveExercise";
import BookmarkButton from './BookmarkButton';
import PlaceholderImage from '../PlaceholderImage';
import { MUSCLE_GROUP_MAP, MUSCLE_GROUPS, getMappedMuscleGroups } from "../../utils/muscleGroups";
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useImageHover } from "../../hooks/useImageHover";

const muscleGroups = MUSCLE_GROUPS;
const muscleGroupMap = MUSCLE_GROUP_MAP;


function Library({ user, onRepeatWorkout }) {
  const [exercises, setExercises] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Check if we should show workouts tab (e.g., after saving a custom workout)
    const showWorkoutsTab = localStorage.getItem('showWorkoutsTab');
    if (showWorkoutsTab === 'true') {
      localStorage.removeItem('showWorkoutsTab');
      return "workouts";
    }
    return "exercises";
  });
  const [sortBy, setSortBy] = useState("");
  const [highlightedWorkout, setHighlightedWorkout] = useState(null);
  
  // Use the custom hook for image hover functionality
  const { hoveredId, setHoveredId, activeIndex, setActiveIndex } = useImageHover();

  const savedExerciseIds = exercises.map((ex) => ex.id);

  // Check for workout to highlight and tab activation
  useEffect(() => {
    const highlightWorkout = localStorage.getItem('highlightNewWorkout');
    if (highlightWorkout) {
      setHighlightedWorkout(highlightWorkout);
      localStorage.removeItem('highlightNewWorkout');
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedWorkout(null);
      }, 3000);
    }
    
    // Also check for tab activation flag (in case of timing issues)
    const showWorkoutsTab = localStorage.getItem('showWorkoutsTab');
    if (showWorkoutsTab === 'true') {
      console.log('Library: Setting active tab to workouts (useEffect)');
      setActiveTab('workouts');
      localStorage.removeItem('showWorkoutsTab');
    }
  }, []);

  useEffect(() => {
    const fetchWorkouts = async () => {
      try {
        const data = await getUserWorkouts(user);
        setWorkouts(data);
      } catch (err) {
        console.error("Failed to fetch workouts:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchExercises = async () => {
      try {
        const data = await getUserExercises(user);
        setExercises(data);
      } catch (err) {
        console.error("Failed to fetch exercises:", err);
      }
    };

    if (user) {
      fetchWorkouts();
      fetchExercises();
    }
  }, [user]);


  const sortedExercises = useMemo(() => {
    if (!sortBy) return exercises;
    return [...exercises].sort((a, b) => {
      const aPrimary = a.primaryMuscles?.includes(sortBy) ? 0 : (a.secondaryMuscles?.includes(sortBy) ? 1 : 2);
      const bPrimary = b.primaryMuscles?.includes(sortBy) ? 0 : (b.secondaryMuscles?.includes(sortBy) ? 1 : 2);
      if (aPrimary !== bPrimary) return aPrimary - bPrimary;
      return a.name.localeCompare(b.name);
    });
  }, [exercises, sortBy]);

  if (!user) return <p>Please log in to view your library.</p>;
  if (loading) return <p>Loading your library...</p>;

  const handleToggleSave = async (exerciseId, exerciseName) => {
    try {
      await unsaveExercise(user, exerciseId);
      setExercises((prev) => prev.filter((ex) => ex.id !== exerciseId));
      toast.info(`${exerciseName} removed from your library.`);
    } catch (err) {
      console.error("Failed to unsave exercise:", err);
      toast.error("Something went wrong.");
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4 text-center">My Library</h2>

      {/* Tab buttons */}
      <div className="flex gap-8 mb-6 border-b border-gray-300">
        <button
          onClick={() => setActiveTab("exercises")}
          className={`pb-2 border-b-2 text-sm font-medium ${
            activeTab === "exercises"
              ? "border-green-600 text-green-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Saved Exercises
        </button>
        <button
          onClick={() => setActiveTab("workouts")}
          className={`pb-2 border-b-2 text-sm font-medium ${
            activeTab === "workouts"
              ? "border-green-600 text-green-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Saved Workouts
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "workouts" ? (
        workouts.length === 0 ? (
          <p>No workouts saved yet.</p>
        ) : (
          <ul className="space-y-4 text-gray-600">
  {[...workouts].map((w, index) => {
    // Collect all primary/secondary muscles from exercises in this workout
    const allPrimary = w.exercises.flatMap(ex => ex.primaryMuscles || []);
    const allSecondary = w.exercises.flatMap(ex => ex.secondaryMuscles || []);
    const mappedGroups = getMappedMuscleGroups(allPrimary, allSecondary);

     // Format date (Month Day) — use Firestore createdAt
    const date = (() => {
      const ts = w.createdAt || w.date; // fallback to w.date if needed
      if (!ts) return null;
      if (typeof ts.toDate === "function") return ts.toDate();                // Firestore Timestamp
      if (typeof ts === "object" && typeof ts.seconds === "number") return new Date(ts.seconds * 1000); // {seconds, nanoseconds}
      return new Date(ts);                                                    // JS Date or ISO string/number
    })();

    const dateStr = date
      ? date.toLocaleDateString("en-US", { month: "long", day: "numeric" })
      : "";


    const isHighlighted = highlightedWorkout === w.name;
    
    return (
      <li 
        key={index} 
        className={`border p-4 rounded bg-white shadow transition-all duration-1000 ${
          isHighlighted 
            ? 'ring-2 ring-blue-500 bg-blue-50 animate-pulse' 
            : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            {/* Date as overline */}
            <div className="text-[11px] uppercase tracking-wide font-bold text-gray-400 mb-1">
              {dateStr}
            </div>

            {/* Workout Name */}
            {w.name && (
              <div className="text-base font-semibold text-gray-900 mb-2">
                {w.name}
              </div>
            )}

            {/* Tags under date */}
            <div className="flex flex-wrap gap-1 mt-2 mb-2">
              {mappedGroups.map((group, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700"
                >
                  {group.charAt(0).toUpperCase() + group.slice(1)}
                </span>
              ))}
            </div>
          </div>

          {/* Repeat workout button */}
          <button
            onClick={() => onRepeatWorkout({ ...w, fromLibrary: true })}
            className="text-sm bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
          >
            Do This Workout
          </button>
        </div>

        {/* List of exercises */}
        <ul className="list-disc list-inside mt-2">
          {w.exercises.map((ex, i) => (
            <li key={i}>{ex.name}</li>
          ))}
        </ul>
      </li>
    );
  })}
</ul>

        )
      ) : (

        <div className="text-gray-600">
          {exercises.length === 0 ? (
            <p>No exercises saved yet.</p>
          ) : (
            <>
              {/* Sort dropdown */}
              <div className="flex items-center mb-4">
                <label htmlFor="sortBy" className="mr-2 text-sm text-gray-700">
                  Sort by:
                </label>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border rounded px-2 h-9 text-sm"
                >
                  <option value="">-- Select muscle group --</option>
                  {muscleGroups.map((group) => (
                    <option key={group} value={group}>
                      {group.charAt(0).toUpperCase() + group.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <ul className="space-y-4">
                {sortedExercises.map((ex, index) => {
                  const mappedGroups = getMappedMuscleGroups(ex.primaryMuscles, ex.secondaryMuscles);
                  const isMatch =
                    sortBy &&
                    (ex.primaryMuscles?.includes(sortBy) || ex.secondaryMuscles?.includes(sortBy));

                  return (
                    <li key={index} className="group relative">
                      {/* Bookmark toggle button - outside Link to prevent conflicts */}
                      <div className="absolute top-2 right-2 z-20">
                        <BookmarkButton
                          isSaved={savedExerciseIds.includes(ex.id)}
                          onToggle={() => handleToggleSave(ex.id, ex.name)}
                        />
                      </div>

                      <Link
                        to={`/exercise/${ex.id}?from=saved`}
                        className="block"
                      >
                        <div
                          className={`border p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] relative cursor-pointer flex items-start justify-between ${
                            isMatch ? "bg-white" : sortBy ? "bg-gray-50" : "bg-white"
                          } border-gray-100 hover:border-green-200`}
                          onMouseEnter={() => setHoveredId(ex.id)}
                          onMouseLeave={() => setHoveredId(null)}
                        >

                          {/* Left: text content */}
                          <div className="flex-1 pr-4">
                            {/* Title */}
                            <h3 className="text-lg font-semibold">{ex.name}</h3>

                            {/* Category and level */}
                            <p className="text-sm text-gray-600">
                              {ex.category} | {ex.level}
                            </p>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-1 mt-2 mb-2">
                              {mappedGroups.map((group, i) => {
                                const isGroupMatch = sortBy && group.toLowerCase() === sortBy.toLowerCase();
                                return (
                                  <span
                                    key={i}
                                    className={`text-xs px-2 py-1 rounded-full ${
                                      isGroupMatch
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-200 text-gray-700"
                                    }`}
                                  >
                                    {group.charAt(0).toUpperCase() + group.slice(1)}
                                  </span>
                                );
                              })}
                            </div>

                            {/* Sets & reps */}
                            {ex.repRange && (
                              <div className="mt-1">
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(ex.repRange).slice(0, 2).map(([goal, range]) => {
                                    const goalIcons = {
                                      'strength': '💪',
                                      'muscle_building': '🏋️',
                                      'endurance': '🏃',
                                      'power': '⚡',
                                      'flexibility': '🧘',
                                      'mobility': '🔄',
                                      'recovery': '😌',
                                      'cardio': '❤️',
                                      'hiit': '🔥',
                                      'conditioning': '⚡',
                                      'explosive': '💥',
                                      'technique': '🎯',
                                      'stability': '⚖️',
                                      'activation': '🔋'
                                    };
                                    
                                    return (
                                      <span
                                        key={goal}
                                        className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center gap-1"
                                      >
                                        <span>{goalIcons[goal] || '💪'}</span>
                                        <span>{range}</span>
                                      </span>
                                    );
                                  })}
                                  {Object.keys(ex.repRange).length > 2 && (
                                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                      +{Object.keys(ex.repRange).length - 2} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Click indicator text */}
                            <div className="flex items-center gap-1 mt-2 text-xs text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <span>View details</span>
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>

                          {/* Right: crossfading image with click indicator */}
                          <div className="relative w-24 h-24 shrink-0">
                            {ex.images?.slice(0, 2).map((img, i) => (
                              img === "placeholder.jpg" ? (
                                <PlaceholderImage 
                                  key={i}
                                  className={`absolute inset-0 w-full h-full rounded border ${
                                    (hoveredId === ex.id && activeIndex === i) ||
                                    (hoveredId !== ex.id && i === 0)
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                />
                              ) : (
                                <img
                                  key={i}
                                  src={`/exercise-images/${img}`}
                                  alt={`${ex.name} ${i + 1}`}
                                  className={`absolute inset-0 w-full h-full object-cover rounded border transition-opacity duration-700 ${
                                    (hoveredId === ex.id && activeIndex === i) ||
                                    (hoveredId !== ex.id && i === 0)
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                />
                              )
                            ))}
                            {/* Click indicator overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded border flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <svg
                                  className="w-6 h-6 text-white drop-shadow-lg"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Library;
